import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

/* ------------------------------------------------------------------
   Single source of truth for the app theme.

   The theme has two independent axes:

     appearance  "light" | "dark" | "system"   (localStorage "theme")
     style       "glass" | "classic"           (localStorage "themeStyle")

   "system" follows the OS (prefers-color-scheme) live. "glass" is the
   Liquid Glass look — translucent, blurred chrome floating over an ambient
   wallpaper; "classic" is the flat opaque look. Glass is the default for
   anyone who hasn't chosen.

   Two consumers have to stay in sync, which is why the resolved `dark`
   class is written to BOTH <html> and <body>:

     - <html>.dark  drives Tailwind's `dark:` variants (darkMode: "class")
       and is what the many `document.documentElement.classList` reads in
       chart/icon code look at.
     - <body>.dark  drives every `body.dark ...` rule in index.css
       (.app-chrome, .app-card, .app-canvas, and friends).

   The style lands on <html> only, as `glass` — index.css keys every glass
   token off `html.glass`, and Tailwind's `glass:` variant matches it.

   Writing only one of them is what caused the old white-on-white bugs.
   The pre-paint script in public/index.html mirrors applyTheme() — keep the
   two in step.
------------------------------------------------------------------ */

const STORAGE_KEY = "theme";
const STYLE_KEY = "themeStyle";

const MODES = ["light", "dark", "system"];
const STYLES = ["glass", "classic"];

const darkQuery = () =>
  typeof window !== "undefined" && window.matchMedia
    ? window.matchMedia("(prefers-color-scheme: dark)")
    : null;

const systemPrefersDark = () => Boolean(darkQuery()?.matches);

/** Resolved theme ("light" | "dark") for any stored/requested value. */
export function normalizeTheme(value) {
  if (value === "system") return systemPrefersDark() ? "dark" : "light";
  return value === "dark" ? "dark" : "light";
}

const normalizeMode = (value) => (MODES.includes(value) ? value : "system");
const normalizeStyle = (value) => (STYLES.includes(value) ? value : "glass");

const readStorage = (key) => {
  try {
    return window.localStorage.getItem(key);
  } catch {
    // Private-mode / blocked storage.
    return null;
  }
};

const writeStorage = (key, value) => {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* storage blocked — the in-memory theme still works for this tab */
  }
};

/** Stored appearance choice. Anyone who never chose follows the OS. */
export function getStoredMode() {
  return normalizeMode(readStorage(STORAGE_KEY));
}

/** Resolved theme for the stored appearance ("light" | "dark"). */
export function getStoredTheme() {
  return normalizeTheme(getStoredMode());
}

/** Stored visual style. Defaults to Liquid Glass. */
export function getStoredStyle() {
  return normalizeStyle(readStorage(STYLE_KEY));
}

// Matches the canvas each theme paints, so mobile browser chrome (iOS
// Safari's toolbar tint, Android's status bar) blends with the page.
const THEME_COLORS = {
  "light-glass": "#e8eef7",
  "dark-glass": "#0b1020",
  "light-classic": "#eef2f7",
  "dark-classic": "#121827",
};

/** Write the `dark`/`glass` classes + color-scheme to the document. */
export function applyTheme(theme, style = getStoredStyle()) {
  const isDark = normalizeTheme(theme) === "dark";
  const isGlass = normalizeStyle(style) === "glass";
  const root = document.documentElement;

  root.classList.toggle("dark", isDark);
  document.body?.classList.toggle("dark", isDark);
  root.classList.toggle("glass", isGlass);

  // Makes native UI (scrollbars, form controls, autofill) follow the theme.
  root.style.colorScheme = isDark ? "dark" : "light";

  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) {
    meta.setAttribute(
      "content",
      THEME_COLORS[`${isDark ? "dark" : "light"}-${isGlass ? "glass" : "classic"}`]
    );
  }
}

const ThemeContext = createContext({
  theme: "light",
  mode: "system",
  style: "glass",
  isDark: false,
  isGlass: true,
  setTheme: () => {},
  toggleTheme: () => {},
  setStyle: () => {},
});

export function ThemeProvider({ children }) {
  const [mode, setModeState] = useState(getStoredMode);
  const [style, setStyleState] = useState(getStoredStyle);
  // Tracks the OS setting so "system" re-renders when it flips.
  const [systemDark, setSystemDark] = useState(systemPrefersDark);

  useEffect(() => {
    const query = darkQuery();
    if (!query) return undefined;
    const onChange = (event) => setSystemDark(event.matches);
    // Safari < 14 only has the deprecated addListener.
    if (query.addEventListener) query.addEventListener("change", onChange);
    else query.addListener(onChange);
    return () => {
      if (query.removeEventListener)
        query.removeEventListener("change", onChange);
      else query.removeListener(onChange);
    };
  }, []);

  const theme =
    mode === "system" ? (systemDark ? "dark" : "light") : normalizeTheme(mode);

  // Apply on mount and on every change. The pre-paint script in index.html
  // already set the correct classes, so the first run is a no-op in practice —
  // it exists so the classes are still correct if that script is ever removed.
  useEffect(() => {
    applyTheme(theme, style);
  }, [theme, style]);

  useEffect(() => writeStorage(STORAGE_KEY, mode), [mode]);
  useEffect(() => writeStorage(STYLE_KEY, style), [style]);

  // Keep multiple tabs in agreement.
  useEffect(() => {
    const onStorage = (event) => {
      if (event.key === STORAGE_KEY && event.newValue) {
        setModeState(normalizeMode(event.newValue));
      }
      if (event.key === STYLE_KEY && event.newValue) {
        setStyleState(normalizeStyle(event.newValue));
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  /** Accepts "light" | "dark" | "system". */
  const setTheme = useCallback((next) => {
    setModeState(normalizeMode(next));
  }, []);

  // Flips the *resolved* theme and pins it, so a "system" user who hits the
  // toggle gets the opposite of what they're looking at.
  const toggleTheme = useCallback(() => {
    setModeState(theme === "dark" ? "light" : "dark");
  }, [theme]);

  /** Accepts "glass" | "classic". */
  const setStyle = useCallback((next) => {
    setStyleState(normalizeStyle(next));
  }, []);

  const value = useMemo(
    () => ({
      theme,
      mode,
      style,
      isDark: theme === "dark",
      isGlass: style === "glass",
      setTheme,
      toggleTheme,
      setStyle,
    }),
    [theme, mode, style, setTheme, toggleTheme, setStyle]
  );

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext);
}

/**
 * Reactive replacement for `document.documentElement.classList.contains("dark")`.
 *
 * Those direct DOM reads happen during render, so they could never notice a
 * theme change — which is why the old toggle had to reload the page. Reading
 * from context instead re-renders the consumer on toggle, no reload needed.
 */
export function useIsDarkMode() {
  return useContext(ThemeContext).isDark;
}

/** True while the Liquid Glass style is active. */
export function useIsGlass() {
  return useContext(ThemeContext).isGlass;
}
